import { LogOut, UserCheck, X } from "lucide-react";
import ThemeSwitcher from "./ThemeSwitcher";
import LanguageSwitcher from "./LanguageSwitcher";
import DesktopConsoleBindingCard from "./DesktopConsoleBindingCard";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useContextStore } from "@store/context";
import { cn } from "@/utils/cn";
import BrandLogo from "./BrandLogo";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/providers/AuthProvider";
import { toast } from "@/providers/NotificationProvider";
import {
  getHankoElementsLanguage,
  registerHankoElements,
} from "@/services/hankoAuth";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import ShellNavTree from "@/components/shell/ShellNavTree";
import { MY_ACCESS_ROUTE } from "@/components/organization/orgRoutes";

interface SidebarProps {
  open: boolean;
  width: number;
  mobile?: boolean;
  onToggle: () => void;
}

export default function Sidebar({
  open,
  width,
  mobile = false,
  onToggle,
}: SidebarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const org = useContextStore((state) => state.org);
  const workspace = useContextStore((state) => state.workspace);
  const spaces = useContextStore((state) => state.spaces);
  const organizationsInContext = useContextStore(
    (state) => state.organizations,
  );
  const { role } = usePermissions();
  const { authMode, logout, user, userProfile } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const currentWorkspace = useMemo(
    () => spaces.find((item) => item.id === workspace) || null,
    [spaces, workspace],
  );
  const currentOrganization = useMemo(
    () => organizationsInContext.find((item) => item.id === org) || null,
    [org, organizationsInContext],
  );
  const displayName =
    userProfile?.displayName || user?.displayName || user?.email || "User";
  const userEmail = userProfile?.email || user?.email || "No email";
  const authLabel =
    authMode === "local"
      ? t("sidebar.localAuthentication")
      : t("sidebar.hankoAuthentication");
  const authSignOutHint =
    authMode === "local"
      ? t("sidebar.localSignOutHint")
      : t("sidebar.hankoSignOutHint");
  const profileDescription =
    authMode === "hanko"
      ? t("sidebar.hankoProfileDescription")
      : authSignOutHint;
  const hankoLanguage = getHankoElementsLanguage(
    i18n.resolvedLanguage || i18n.language,
  );
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
  const osVersion =
    typeof __APP_VERSION__ === "string" && __APP_VERSION__.trim()
      ? __APP_VERSION__
      : "dev";
  const profileContext = [
    currentOrganization?.name,
    currentWorkspace?.name,
  ]
    .filter(Boolean)
    .join(" · ");

  useEffect(() => {
    if (!profileOpen || authMode !== "hanko") {
      return;
    }
    registerHankoElements().catch((error) => {
      console.error("Failed to load account security element:", error);
      toast.error(t("sidebar.hankoProfileLoadError"));
    });
  }, [authMode, profileOpen, t]);

  const handleLogout = async () => {
    if (loggingOut) {
      return;
    }

    setLoggingOut(true);
    try {
      await logout();
      setLogoutConfirmOpen(false);
      setProfileOpen(false);
      navigate("/", { replace: true });
      toast.success(authSignOutHint);
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error(
        error instanceof Error ? error.message : t("sidebar.signOut"),
      );
    } finally {
      setLoggingOut(false);
    }
  };

  const expanded = mobile ? true : open;

  return (
    <div
      className={cn(
        "bg-background border-r border-border transition-all duration-200 ease-in-out flex flex-col",
        mobile
          ? "fixed inset-y-0 left-0 z-40 w-[min(82vw,320px)] max-w-[320px]"
          : open
            ? "w-64"
            : "hidden",
        mobile &&
          (open ? "translate-x-0" : "-translate-x-full pointer-events-none"),
      )}
      style={!mobile && open ? { width: `${width}px` } : undefined}
    >
      {/* Header */}
      <div className="flex h-16 items-center justify-between gap-2 border-b border-border px-3">
        <div className="min-w-0">
          <BrandLogo className="max-w-[11rem]" />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          data-testid="shell-nav-close"
          onClick={onToggle}
          aria-label={t("shell.closeNavigation")}
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-1">
        <ShellNavTree
          expanded={expanded}
          mobile={mobile}
          onNavigate={onToggle}
        />

      </nav>

      {/* Footer */}
      <div
        className={cn(
          "border-t border-border p-2 space-y-2",
          expanded ? "" : "flex flex-col items-center",
        )}
      >
        {user && (
          <>
            <button
              type="button"
              onClick={() => setProfileOpen(true)}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 text-left transition-colors hover:bg-card-raised",
                expanded ? "" : "w-12 justify-center px-0",
              )}
              title={
                !expanded
                  ? `${displayName} · ${t("sidebar.userProfile")}`
                  : undefined
              }
            >
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-foreground/10 text-xs font-semibold text-foreground">
                {initials || "U"}
              </div>
              {expanded && (
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">
                    {displayName}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {authMode === "local"
                      ? t("sidebar.localProfile")
                      : userEmail}
                  </div>
                </div>
              )}
            </button>

            <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
              <DialogContent
                className={cn(
                  "flex w-[min(96vw,28rem)] max-w-md flex-col gap-0 overflow-hidden p-0",
                  authMode === "hanko"
                    ? "h-[min(42rem,calc(100vh-2rem))]"
                    : "max-h-[calc(100vh-2rem)]",
                )}
              >
                <DialogHeader className="sr-only">
                  <DialogTitle>{t("sidebar.userProfile")}</DialogTitle>
                  <DialogDescription>{profileDescription}</DialogDescription>
                </DialogHeader>

                <div className="shrink-0 border-b border-border px-6 py-5">
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-soft text-base font-semibold text-accent-foreground">
                      {initials || "U"}
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="min-w-0">
                        <div className="truncate text-base font-semibold">
                          {displayName}
                        </div>
                        <div className="truncate text-sm text-muted-foreground">
                          {userEmail}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <span className="rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-accent-foreground">
                          {role}
                        </span>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                          {authLabel}
                        </span>
                      </div>
                      {profileContext ? (
                        <div className="truncate text-xs text-muted-foreground">
                          {profileContext}
                        </div>
                      ) : null}
                      <div
                        className="text-xs text-muted-foreground"
                        data-testid="sidebar-os-version"
                      >
                        {t("sidebar.osVersion")} {osVersion}
                      </div>
                    </div>
                  </div>
                </div>

                {authMode === "hanko" ? (
                  <Tabs
                    defaultValue="account"
                    className="flex min-h-0 flex-1 flex-col"
                  >
                    <div className="shrink-0 px-6 pt-4">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger
                          value="account"
                          data-testid="sidebar-profile-account"
                        >
                          {t("sidebar.account")}
                        </TabsTrigger>
                        <TabsTrigger
                          value="security"
                          data-testid="sidebar-profile-security"
                        >
                          {t("sidebar.profileSecurity")}
                        </TabsTrigger>
                      </TabsList>
                    </div>
                    <div className="relative min-h-0 flex-1">
                      <TabsContent
                        forceMount
                        value="account"
                        className="absolute inset-0 space-y-4 overflow-y-auto px-6 py-4 data-[state=inactive]:invisible data-[state=inactive]:pointer-events-none"
                      >
                        <section
                          className="space-y-3 rounded-xl border border-border bg-muted/20 p-4"
                          data-testid="sidebar-profile-preferences"
                        >
                          <h3 className="text-sm font-semibold">
                            {t("sidebar.appearance")}
                          </h3>
                          <ThemeSwitcher layout="row" />
                          <LanguageSwitcher layout="row" />
                        </section>
                        <DesktopConsoleBindingCard />
                      </TabsContent>
                      <TabsContent
                        forceMount
                        value="security"
                        className="absolute inset-0 overflow-y-auto px-6 py-4 data-[state=inactive]:invisible data-[state=inactive]:pointer-events-none"
                      >
                        <section className="min-h-full rounded-xl border border-border bg-muted/20 p-4">
                          <hanko-profile
                            className="agent-hanko-profile"
                            lang={hankoLanguage}
                          />
                        </section>
                      </TabsContent>
                    </div>
                  </Tabs>
                ) : (
                  <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
                    <section
                      className="space-y-3 rounded-xl border border-border bg-muted/20 p-4"
                      data-testid="sidebar-profile-preferences"
                    >
                      <h3 className="text-sm font-semibold">
                        {t("sidebar.appearance")}
                      </h3>
                      <ThemeSwitcher layout="row" />
                      <LanguageSwitcher layout="row" />
                    </section>
                    <DesktopConsoleBindingCard />
                  </div>
                )}

                <DialogFooter className="shrink-0 border-t border-border px-6 py-4 sm:justify-between">
                  {/* Run › Mes accès is a profile entry, not a tree node (OS-25). */}
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="sidebar-profile-my-access"
                    onClick={() => {
                      setProfileOpen(false);
                      const params = new URLSearchParams();
                      if (org) params.set("organizationId", org);
                      const search = params.toString();
                      navigate(
                        search ? `${MY_ACCESS_ROUTE}?${search}` : MY_ACCESS_ROUTE,
                      );
                      if (mobile) onToggle();
                    }}
                  >
                    <UserCheck className="mr-2 h-4 w-4" />
                    {t("org.myAccess.entry")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    title={authSignOutHint}
                    onClick={() => setLogoutConfirmOpen(true)}
                    disabled={loggingOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {authMode === "local"
                      ? t("sidebar.switchUser")
                      : t("sidebar.signOut")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog
              open={logoutConfirmOpen}
              onOpenChange={setLogoutConfirmOpen}
            >
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{t("sidebar.confirmSignOutTitle")}</DialogTitle>
                  <DialogDescription>
                    {authMode === "local"
                      ? t("sidebar.confirmSwitchUserDescription")
                      : t("sidebar.confirmSignOutDescription")}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2 sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLogoutConfirmOpen(false)}
                    disabled={loggingOut}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => void handleLogout()}
                    disabled={loggingOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {authMode === "local"
                      ? t("sidebar.switchUser")
                      : t("sidebar.signOut")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        <div
          className={cn(
            "text-xs text-muted-foreground",
            expanded ? "px-2" : "text-center",
          )}
        >
          {t("sidebar.access")} · {role}
        </div>
      </div>
    </div>
  );
}
